import React, { useEffect, useRef, useState } from 'react';
import { RowItem } from '../recs/engine';
import { UserProfile } from '../data/types';
import { useAnalytics } from '../recs/analytics';
import './PlaybackControl.css';

interface Props {
  item: RowItem;
  viewer: UserProfile;
  onWhyClick: () => void;
}

export default function PlaybackControl({ item, viewer, onWhyClick }: Props) {
  const { title, progressPct } = item;
  const isPremiumLocked = title.access_tier === 'Premium' && viewer.access_tier === 'Free';

  const [playing, setPlaying] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const { logPlayback } = useAnalytics();
  const elapsedRef = useRef(0);
  elapsedRef.current = elapsedSeconds;
  const playingRef = useRef(false);
  playingRef.current = playing;

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    // Logs a final "pause" if the viewer navigates away mid-playback, so a
    // play with no matching pause never gets left dangling. Runs only on
    // unmount (empty deps + refs) — not on every play/pause toggle.
    return () => {
      if (playingRef.current) {
        logPlayback({ titleId: title.title_id, titleName: title.title_name, action: 'pause', elapsedSeconds: elapsedRef.current });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePlayback = () => {
    const next = !playing;
    setPlaying(next);
    logPlayback({
      titleId: title.title_id,
      titleName: title.title_name,
      action: next ? 'play' : 'pause',
      elapsedSeconds: elapsedRef.current,
    });
  };

  return (
    <div className="playback-control">
      {playing && (
        <div className="playback-control__video">
          <iframe
            key={title.youtube_id}
            src={`https://www.youtube-nocookie.com/embed/${title.youtube_id}?autoplay=1&rel=0&modestbranding=1`}
            title={title.title_name}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            frameBorder={0}
          />
        </div>
      )}
      <div className="playback-control__row">
        <button
          className="playback-control__play-btn"
          onClick={togglePlayback}
          disabled={isPremiumLocked}
          aria-pressed={playing}
        >
          {playing ? '❚❚ Pause' : '▶ Play'}
        </button>
        <button className="playback-control__why-btn" onClick={onWhyClick} type="button">
          ✨ Why this?
        </button>
        {typeof progressPct === 'number' && (
          <span className="playback-control__progress">{Math.round(progressPct)}% watched</span>
        )}
      </div>
      <p className="playback-control__hint">
        {isPremiumLocked
          ? 'Upgrade to Premium to play'
          : `${Math.floor(elapsedSeconds / 60)}:${(elapsedSeconds % 60).toString().padStart(2, '0')} — logged as a play/pause signal`}
      </p>
    </div>
  );
}
